import React from 'react';
import {
    Typography,
    Container,
    Box,
    Button,
    Stack,
    Alert,
    Card,
    CardContent,
    CardActions,
    Divider,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { getCurrentBusinessConfig, getStripeAccount } from '../firebase/firebase-delegate';
import LoadingSpinner from '../components/global/LoadingSpinner';
import { useAdminData } from '../contexts/AdminDataContext';
import { useTenant } from '../tenancy/TenantContext';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase/firebase';
import { createControlPlaneTenantUrl } from '../tenancy/tenantUrls';


const PaymentPortalPage: React.FC = () => {
    const { tenantId } = useTenant();
    // ===== Queries =====
    const { currentEmployeeQuery: employeeQuery } = useAdminData();

    const employeeStripeQuery = useQuery({
        queryKey: ['currentEmployeeStripe', tenantId, employeeQuery.data?.id],
        enabled: !!employeeQuery.data?.id,
        queryFn: () => getStripeAccount(employeeQuery.data!.id, tenantId),
        staleTime: 0,
        refetchOnMount: 'always',
        refetchOnWindowFocus: true,
    });

    const businessConfigQuery = useQuery({
        queryKey: ['businessConfig', tenantId],
        queryFn: () => getCurrentBusinessConfig(tenantId),
        staleTime: 0,
        refetchOnMount: 'always',
        refetchOnWindowFocus: true,
    });
    const connectMutation = useMutation({
        mutationFn: async () => {
            const createLink = httpsCallable<
              {tenantId: string; returnUrl: string},
              {url: string}
            >(functions, 'createStripeConnectLink');
            const result = await createLink({
                tenantId,
                returnUrl: createControlPlaneTenantUrl('/admin/payment-portal', tenantId),
            });
            return result.data.url;
        },
        onSuccess: (url) => window.location.assign(url),
    });

    // ===== Loading / Error states =====
    if (employeeQuery.isLoading || employeeStripeQuery.isLoading || businessConfigQuery.isLoading) {
        return <LoadingSpinner label="Loading payment" />;
    }

    if (employeeQuery.isError || employeeStripeQuery.isError || businessConfigQuery.isError) {
        return (
            <Container maxWidth="sm">
                <Box sx={{ mt: 6, textAlign: 'center' }}>
                    <Alert severity="error">Failed to load payment settings. Please refresh and try again.</Alert>
                </Box>
            </Container>
        );
    }

    // ===== Derived state =====
    const employeeId = employeeQuery.data!.id;
    const cfg = businessConfigQuery.data;

    const isOwner = cfg?.paymentInfo?.owner === employeeId;
    const useBusinessStripe = cfg?.payoutInfo?.useBusinessStripeAccount === true;
    const businessStripeAccountId: string | null = cfg?.payoutInfo?.businessStripeAccountId || null;

    const rawEmployeeStripeId: string | null = employeeStripeQuery.data?.stripe_account_id || null;

    // Only flag "business-owned" if a business account exists AND the employee doc has that same ID
    const isBusinessIdOnEmployeeDoc =
        useBusinessStripe && !!businessStripeAccountId && rawEmployeeStripeId === businessStripeAccountId;

    // Employee has their own Stripe if they have an ID and it isn’t the business’s
    const hasEmployeeStripe = !!rawEmployeeStripeId && !isBusinessIdOnEmployeeDoc;

    console.log('all the values: ', {
        employeeId,
        isOwner,
        useBusinessStripe,
        businessStripeAccountId,
        rawEmployeeStripeId,
        isBusinessIdOnEmployeeDoc,
        hasEmployeeStripe,
    });

    // ===== Views =====
    // 🚫 Business-level payouts enabled and user is NOT owner → block employee setup
    if (useBusinessStripe && !isOwner) {
        return (
            <Container maxWidth="sm">
                <Box sx={{ mt: 6 }}>
                    <Alert severity="warning" sx={{ mb: 3 }}>
                        Payments are configured at the business level. Personal (employee) Stripe connections are disabled. Please
                        speak to an admin if you have questions.
                    </Alert>
                    <Card elevation={0} variant="outlined">
                        <CardContent>
                            <Typography variant="h5" gutterBottom>
                                Business payouts status
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {businessStripeAccountId ? 'A business Stripe account is connected.' : 'The business Stripe account is not connected yet.'}
                            </Typography>
                        </CardContent>
                    </Card>
                </Box>
            </Container>
        );
    }

    // 👑 Owner + business payouts enabled → owner informational view
    if (useBusinessStripe && isOwner) {
        return (
            <Container maxWidth="sm">
                <Box sx={{ mt: 6 }}>
                    <Card elevation={0} variant="outlined">
                        <CardContent>
                            <Typography variant="h4" gutterBottom>
                                Business Payouts Enabled
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 2 }}>
                                This site uses a business Stripe account for payouts.
                            </Typography>
                            <Box sx={{ bgcolor: 'grey.100', p: 1.5, borderRadius: 1, fontFamily: 'monospace', fontSize: 13, display: 'inline-block' }}>
                                {businessStripeAccountId ? `Connected Business Account: ${businessStripeAccountId}` : 'No business Stripe account connected yet. Set this in the Business Configuration.'}
                            </Box>
                        </CardContent>
                        <Divider />
                        <CardActions sx={{ justifyContent: 'center', pb: 2 }}>
                            <Button variant="contained" color="primary" component="a" href="https://dashboard.stripe.com" target="_blank" rel="noopener noreferrer">
                                Go to Stripe Dashboard
                            </Button>
                        </CardActions>
                    </Card>
                </Box>
            </Container>
        );
    }

    // 🧑‍💼 Employee-only flow (business payouts NOT enabled)
    return (
        <Container maxWidth="sm">
            <Box sx={{ mt: 6 }}>
                {!hasEmployeeStripe ? (
                    <Card elevation={0} variant="outlined">
                        <CardContent>
                            <Typography variant="h4" gutterBottom>
                                Set Up Payments
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 1.5 }}>
                                Connect a Stripe account to receive payments directly through this app.
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                Stripe charges 2.9% + 30¢ per transaction (e.g., $50 → $48.25 after fees). You will manage payouts,
                                refunds, and settings in your Stripe dashboard.
                            </Typography>
                        </CardContent>
                        <Divider />
                        <CardActions sx={{ justifyContent: 'center', py: 2 }}>
                            <Button variant="contained" color="primary"
                              disabled={connectMutation.isPending}
                              onClick={() => connectMutation.mutate()}>
                                {connectMutation.isPending ? 'Opening Stripe…' : 'Connect with Stripe'}
                            </Button>
                            {connectMutation.isError && <Alert severity="error">Could not start Stripe setup. Please try again.</Alert>}
                        </CardActions>
                    </Card>
                ) : (
                    <Card elevation={0} variant="outlined">
                        <CardContent>
                            <Stack spacing={1.5} alignItems="center">
                                <Typography variant="h4" color="primary.main">
                                    Your Stripe account is active!
                                </Typography>
                                <Typography variant="body2" color="text.secondary" align="center">
                                    You can view payouts, manage settings, and handle refunds in your Stripe dashboard.
                                </Typography>
                            </Stack>
                        </CardContent>
                        <Divider />
                        <CardActions sx={{ justifyContent: 'center', py: 2 }}>
                            <Button variant="contained" color="primary" component="a" href="https://dashboard.stripe.com" target="_blank" rel="noopener noreferrer">
                                Go to Stripe Dashboard
                            </Button>
                        </CardActions>
                    </Card>
                )}
            </Box>
        </Container>
    );
};

export default PaymentPortalPage;
